import FootballGameContext from "../../../../../sports/football/context/football-game-context";
import { ContextSerializer } from "../ContextSerializer";
import { formatMilliseconds } from "../../../../common/date-util";
import { DeuceFragmentSerializer } from "../fragments/DeuceFragmentSerializer";
import { DownedFragmentSerializer } from "../fragments/DownedFragmentSerializer";
import { DriveFragmentSerializer } from "../fragments/DriveFragmentSerializer";
import { FairCatchFragmentSerializer } from "../fragments/FairCatchFragmentSerializer";
import { FieldGoalAttemptFragmentSerializer } from "../fragments/FieldGoalAttemptFragmentSerializer";
import { FumbleFragmentSerializer } from "../fragments/FumbleFragmentSerializer";
import { KickOffFragmentSerializer } from "../fragments/KickOffFragmentSerializer";
import { NoPlayFragmentSerializer } from "../fragments/NoPlayFragmentSerializer";
import { OnSideFragmentSerializer } from "../fragments/OnSideFragmentSerializer";
import { OutOfBoundsFragmentSerializer } from "../fragments/OutOfBoundsFragmentSerializer";
import { PassBrokenUpFragmentSerializer } from "../fragments/PassBrokenUpFragmentSerializer";
import { PassFragmentSerializer } from "../fragments/PassFragmentSerializer";
import { PassIncompleteBrokenUpFragmentSerializer } from "../fragments/PassIncompleteBrokenUpFragmentSerializer";
import { PassSackedFragmentSerializer } from "../fragments/PassSackedFragmentSerializer";
import { PatFragmentSerializer } from "../fragments/PatFragmentSerializer";
import { PenaltyFragmentSerializer } from "../fragments/PenaltyFragmentSerializer";
import { PuntBlockedFragmentSerializer } from "../fragments/PuntBlockFragmentSerializer";
import { PuntFragmentSerializer } from "../fragments/PuntFragmentSerializer";
import { ReboundNetFragmentSerializer } from "../fragments/ReboundNetFragmentSerializer";
import { ReturnFragmentSerializer } from "../fragments/ReturnFragmentSerializer";
import { RushFragmentSerializer } from "../fragments/RushFragmentSerializer";
import { SafetyFragmentSerializer } from "../fragments/SafetyFragmentSerializer";
import { SpotFragmentSerializer } from "../fragments/SpotFragmentSerializer";
import { TackleFragmentSerializer } from "../fragments/TackleFragmentSerializer";
import { TimeoutFragmentSerializer } from "../fragments/TimeoutFragmentSerializer";
import { TimeFragmentSerializer } from "../fragments/TimeFragmentSerializer";
import { TouchbackFragmentSerializer } from "../fragments/TouchbackFragmentSerializer";
import { ContinueFragmentSerializer } from "../fragments/ContinueFragmentSerializer";
import { PeriodFragmentSerializer } from "../fragments/PeriodFragmentSerializer";
import { DownFragmentSerializer } from "../fragments/DownFragmentSerializer";
import { LateralFragmentSerializer } from "../fragments/LateralFragmentSerializer";
import { BarBallFragmentSerializer } from "../fragments/BarBallFragmentSerializer";
import { CoinTossFragmentSerializer } from "../fragments/CoinTossFragmentSerializer";
import { getPeriodStartSeconds } from "../../../../../sports/football/utils/context-utils";
import { RougeFragmentSerializer } from "../fragments/RougeFragmentSerializer";
import { SpikeFragmentSerializer } from "../fragments/SpikeFragmentSerializer";
import PassType from "../../../../../sports/football/enums/pass-type";
import RushType from "../../../../../sports/football/enums/rush-type";
import FootballState from "../../../../../sports/football/state/football-state";
import { SkipKickoffFragmentSerializer } from "../fragments/SkipKickoffFragmentSerializer";
import { FumbleSnapFragmentSerializer } from "../fragments/FumbleSnapFragmentSerializer";

const serializers = [
    new BarBallFragmentSerializer(),
    new CoinTossFragmentSerializer(),
    new ContinueFragmentSerializer(),
    new DownFragmentSerializer(),
    new DeuceFragmentSerializer(),
    new DownedFragmentSerializer(),
    new DriveFragmentSerializer(),
    new FairCatchFragmentSerializer(),
    new FieldGoalAttemptFragmentSerializer(),
    new FumbleFragmentSerializer(),
    new FumbleSnapFragmentSerializer(),
    new KickOffFragmentSerializer(),
    new LateralFragmentSerializer(),
    new NoPlayFragmentSerializer(),
    new OnSideFragmentSerializer(),
    new OutOfBoundsFragmentSerializer(),
    new PassBrokenUpFragmentSerializer(),
    new PassFragmentSerializer(),
    new PassIncompleteBrokenUpFragmentSerializer(),
    new PassSackedFragmentSerializer(),
    new PatFragmentSerializer(),
    new PenaltyFragmentSerializer(),
    new PeriodFragmentSerializer(),
    new PuntBlockedFragmentSerializer(),
    new PuntFragmentSerializer(),
    new ReboundNetFragmentSerializer(),
    new ReturnFragmentSerializer(),
    new RougeFragmentSerializer(),
    new RushFragmentSerializer(),
    new SafetyFragmentSerializer(),
    new SkipKickoffFragmentSerializer(),
    new SpikeFragmentSerializer(),
    new SpotFragmentSerializer(),
    new TackleFragmentSerializer(),
    new TimeoutFragmentSerializer(),
    new TimeFragmentSerializer(),
    new TouchbackFragmentSerializer()
];

const INDOOR_PAT_STATES = [
    FootballState.BarBallRecoveredKicking,
    FootballState.BarBallRecoveredReceiving,
    FootballState.ReboundNetCaughtOffense,
    FootballState.ReboundNetCaughtDefense,
    FootballState.ReboundNetFieldedKicking,
    FootballState.ReboundNetFieldedReceiving,
    FootballState.ReboundNetHitGround
];

export class PlaySerializer {
    applicable(play) {
        return true;
    }

    serialize(play, serializationContext) {
        const { gameContext } = serializationContext;
        const { gameClock, gameConfig } = gameContext;
        const { minutesByPeriod } = gameConfig.general;
        const currentTime = gameClock.now();
        const scoringTime = Math.abs(
            getPeriodStartSeconds(minutesByPeriod) * 1000 - currentTime
        );
        const { fragments } = play;
        const currentTimeFormatted = formatMilliseconds(currentTime);
        const scoringTimeFormatted = formatMilliseconds(scoringTime);
        const initialContext = Object.assign(
            new FootballGameContext(),
            gameContext,
            play.getContextSnapshot()
        );

        const rawFragments = [];
        const comments = [];
        let RUSH_TYPE = "";
        let PASS_TYPE = "";
        // StartDrive is still handled here so legacy games (which may carry a
        // StartDrive play) keep serializing it as a drive marker without an
        // extra time fragment.
        const isTimeOrDrivePlay =
            play.historyContains(FootballState.SetTimeAndPeriod) ||
            play.historyContains(FootballState.StartDrive);

        fragments.forEach((fragment) => {
            const serializer = serializers.find((s) =>
                s.applicable(fragment, play, serializationContext)
            );

            const isPatIgnorable =
                fragment.associatedState !== FootballState.PatTry &&
                play.historyContains(FootballState.PatTry) &&
                !INDOOR_PAT_STATES.includes(fragment.associatedState);

            if (serializer && !isPatIgnorable) {
                rawFragments.push(
                    serializer.getRawFragment(
                        play,
                        serializationContext,
                        fragment
                    )
                );
                if (serializer.isCommentSerializer()) {
                    comments.push(
                        serializer.getComment(
                            play,
                            fragment,
                            serializationContext
                        )
                    );
                }

                const code = serializer.getTypeCode(fragment);
                const shouldSerializeTypeCode =
                    code &&
                    ![PassType.SKIP, RushType.SKIP].some(
                        ({ code: c }) => c === code
                    );
                if (shouldSerializeTypeCode) {
                    switch (fragment.associatedState) {
                        case FootballState.Rush:
                            RUSH_TYPE = code;
                            break;
                        case FootballState.Pass:
                            PASS_TYPE = code;
                            break;
                        default:
                            break;
                    }
                }
            }
        });

        if (!isTimeOrDrivePlay) {
            rawFragments.push(
                new TimeFragmentSerializer().getRawFragment(
                    play,
                    serializationContext
                )
            );
        }

        return [
            {
                homeTeam: gameContext.isHomePossession(),
                id: "", // TODO
                period: gameContext.getCurrentPeriod(),
                players: {},
                playtype: comments.length ? "CMT" : "FB",
                sequence: play.index,
                props: {
                    COMMENT: comments.join(),
                    CMT: comments.join(),
                    CLOCK: currentTimeFormatted,
                    SCORE_BOARD_CLOCK: currentTimeFormatted,
                    SCORING_CLOCK: scoringTimeFormatted,
                    RAW_PLAY: rawFragments.join(" "),
                    //TODO: Maybe this is not the right way to instantiate a context.
                    INITIAL_CONTEXT:
                        ContextSerializer.serialize(initialContext),
                    UPDATED_CONTEXT: ContextSerializer.serialize(gameContext),
                    SCORE_AWAY: gameContext.getVisitorScore(),
                    SCORE_HOME: gameContext.getHomeScore(),
                    H_SCORE: gameContext.getHomeScore(),
                    V_SCORE: gameContext.getVisitorScore(),
                    RUSH_TYPE,
                    PASS_TYPE
                }
            }
        ];
    }
}
